/**
 * The named-colleague input for "Shared → Only specific colleagues"
 * (FRONT-1880).
 *
 * Lifted out of the Sharing tab, where it was a separate "Allowlist" block
 * sitting under a separate "Direct share link" card — three controls for one
 * question. It is the people half of the audience now, and it lives where
 * the audience is chosen.
 *
 * **A plain inline field, deliberately not a popover.** The first cut reused
 * the old allowlist's `Popover` + `Command` combobox, which put a portalled
 * Radix layer inside another portalled Radix layer — and inside the row
 * menu's shell, inside a focus-trapping dialog as well. Nesting dismissable
 * layers that way is how a click on the inner one closes the outer, and how
 * typing lands in the wrong element.
 *
 * FRONT-2667: it now suggests colleagues as you type, from the org people
 * directory (everyone who has signed in to Eddy in this workspace). The list
 * renders INLINE, in normal flow under the field, for the same reason the
 * field is not a combobox popover: no second portalled layer inside the share
 * menu. Typing a full address on the owner's own domain still works — a
 * colleague who has never signed in to Eddy is not in the directory but can
 * still be shared with.
 *
 * FRONT-2756: an entry is an EMAIL, always. A bare word used to be stored as
 * a username, but Eddy never holds a colleague's login name, so it matched
 * nobody while the control said the app was shared. An address on another
 * domain is accepted only when the directory knows that exact address (the
 * person has signed in to this workspace); anything else is refused.
 *
 * The one rule it carries: **an address outside the workspace can never
 * work, and it says so.** `isOnShareAllowlist` (`src/apps/routes.ts`) and
 * the access gate both require the caller to be in the app's org before the
 * list is read at all, so an outside address is stored, displayed, and
 * matches nobody — the owner believes they shared something they did not.
 */
import { useEffect, useId, useState, type KeyboardEvent } from 'react';
import { PlusIcon, UserCircleIcon, XIcon } from '@phosphor-icons/react';
import { cn } from '~/lib/utils';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { usePeopleSearch, type PersonSuggestion } from '~/hooks/queries/use-people-search';
import { moveHighlightIndex } from '~/features/work/person-name-input';

/** Characters typed before the directory is asked. */
export const MIN_PEOPLE_QUERY_LENGTH = 2;
const PEOPLE_SUGGEST_DEBOUNCE_MS = 200;

/**
 * Pure: the suggestions worth showing — the server's matches minus anyone
 * already on the list (compared case-insensitively, the way the access gate
 * compares). Exported for unit testing.
 */
export function visiblePeopleSuggestions(hits: PersonSuggestion[], people: string[]): PersonSuggestion[] {
  const onList = new Set(people.map((p) => p.trim().toLowerCase()));
  return hits.filter((h) => !onList.has(h.email.trim().toLowerCase()));
}

/** A plausible email address — the only thing a named share can match on. */
export function isValidEntry(raw: string): boolean {
  const s = raw.trim();
  if (!s) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

/**
 * Why an address on another domain is refused — or null when the domain is
 * the owner's own.
 *
 * FRONT-2756 (Marko, 2026-10-19): the owner's own domain is accepted as typed;
 * another domain only when the org people directory knows that exact address
 * (the caller checks that, see `SharePeopleInput`), because an org can hold
 * more than one domain and a person who has signed in here is proof of
 * membership. Anything else is refused, not warned: an address outside the
 * workspace can never open the app.
 *
 * Nothing is judged when the owner's own address is unknown — a guess with
 * nothing to compare against is not a finding.
 */
export function allowlistEntryWarning(entry: string, ownDomain: string | null): string | null {
  const at = entry.lastIndexOf('@');
  if (at < 0) return null;
  const domain = entry.slice(at + 1).trim().toLowerCase();
  if (!domain || !ownDomain) return null;
  if (domain === ownDomain.trim().toLowerCase()) return null;
  return `${domain} is not your workspace's domain, so this address can never open the app. To share outside your company, choose Public.`;
}

/** Shown for an address on another domain when the directory could not be asked. */
export const SEARCH_FAILED_FOR_OTHER_DOMAIN =
  'Could not check whether this person is in your workspace. Try again in a moment.';

/**
 * The reason a lone entry that is NOT an email cannot be removed yet
 * (FRONT-2756): removing it would leave a named share with nobody on it, so
 * the replacement goes in first.
 */
export const LONE_STALE_ENTRY_REASON =
  'This entry is not an email and opens the app to nobody. Add a colleague first, then remove it, or choose \u201cOnly you\u201d above.';

/**
 * Pure: may the typed entry be added, and what to say when it may not
 * (FRONT-2756). `directory` is the settled search's rows for what is typed,
 * or null while the search has not answered — then an address on another
 * domain is neither added nor objected to yet, so nothing flickers.
 */
export function judgeTypedEntry(args: {
  typed: string;
  alreadyThere: boolean;
  ownDomain: string | null;
  directory: PersonSuggestion[] | null;
  /** The directory search failed: "not found" would be a guess, so say so. */
  searchFailed?: boolean;
}): { canAdd: boolean; refusal: string | null } {
  const typed = args.typed.trim().toLowerCase();
  if (args.alreadyThere || !isValidEntry(typed)) return { canAdd: false, refusal: null };
  const other = allowlistEntryWarning(typed, args.ownDomain);
  if (!other) return { canAdd: true, refusal: null };
  if (args.searchFailed) return { canAdd: false, refusal: SEARCH_FAILED_FOR_OTHER_DOMAIN };
  if (args.directory === null) return { canAdd: false, refusal: null };
  const known = args.directory.some((p) => p.email.trim().toLowerCase() === typed);
  return known ? { canAdd: true, refusal: null } : { canAdd: false, refusal: other };
}

/**
 * Pure: which row a fresh set of suggestions starts on, or -1 for none.
 *
 * A typed full address is never overridden: only a row with exactly that
 * email is highlighted, so Enter cannot swap "li@acme.com" for "ali@acme.com"
 * (/pr-panel finding). Anything else starts on the first, best-ranked row.
 */
export function initialPeopleHighlight(typed: string, rows: PersonSuggestion[]): number {
  const t = typed.trim().toLowerCase();
  if (t.includes('@')) return rows.findIndex((r) => r.email.trim().toLowerCase() === t);
  return rows.length > 0 ? 0 : -1;
}

export type PeopleEnterAction = { kind: 'pick'; index: number } | { kind: 'add' } | { kind: 'none' };

/**
 * Pure: what Enter does. A highlighted visible row is picked. Otherwise only a
 * typed email address is committed, and only when the Add button would commit
 * it (an address on another domain the directory does not know is refused).
 *
 * A bare word never commits (FRONT-2756): the field reads "Name or email", so
 * a word is a name being searched for, never an entry.
 */
export function decidePeopleEnter(args: {
  typed: string;
  valid: boolean;
  outsideWorkspace: boolean;
  listOpen: boolean;
  rows: PersonSuggestion[];
  highlight: number;
}): PeopleEnterAction {
  if (args.listOpen && args.highlight >= 0 && args.rows[args.highlight]) {
    return { kind: 'pick', index: args.highlight };
  }
  if (args.typed.includes('@') && args.valid && !args.outsideWorkspace) return { kind: 'add' };
  return { kind: 'none' };
}

/**
 * Chips plus one inline field. Both edits apply through the caller (`onAdd` /
 * `onRemove`) rather than a local draft: the audience applies immediately
 * everywhere else in this control, and a list needing its own Save would be
 * the one part of the sharing decision that could sit unsaved.
 *
 * `lastEntryLocked` is the reason the final colleague cannot be removed, or
 * null. Removing them would leave "shared with nobody" — a state that reads
 * as Shared on every badge while opening the app to no one — so the way out
 * is a different audience. It renders as VISIBLE text as well as on the
 * disabled control: a reason that lives only in an `aria-label` is a reason
 * nobody reads.
 */
export function SharePeopleInput({
  people,
  onAdd,
  onRemove,
  ownDomain,
  disabled = false,
  lastEntryLocked = null,
}: {
  people: string[];
  onAdd: (entry: string) => void;
  onRemove: (entry: string) => void;
  ownDomain: string | null;
  disabled?: boolean;
  lastEntryLocked?: string | null;
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [focused, setFocused] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const listboxId = useId();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), PEOPLE_SUGGEST_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const searchable = debounced.length >= MIN_PEOPLE_QUERY_LENGTH;
  const search = usePeopleSearch(debounced, searchable && !disabled);
  // Rows are shown only once the debounced search has caught up with what is
  // typed: rows from the previous keystroke would be a pick the person never
  // asked for, and Enter would take it.
  const settled = debounced === query.trim() && !search.isFetching;
  const suggestions = searchable && settled ? visiblePeopleSuggestions(search.people, people) : [];
  const listOpen = focused && !disabled && suggestions.length > 0;

  const trimmed = query.trim().toLowerCase();

  // A fresh set of rows, or a different query over the same rows, re-derives
  // the starting highlight. Keyed on both: a keystroke that changes the query
  // but returns the same rows must not leave a stale or cleared highlight.
  const suggestionKey = suggestions.map((p) => p.userId).join(',');
  useEffect(() => {
    setHighlight(initialPeopleHighlight(trimmed, suggestions));
    // `suggestions` is fully described by `suggestionKey`.
  }, [suggestionKey, trimmed]);
  const alreadyThere = people.includes(trimmed);
  const valid = isValidEntry(trimmed) && !alreadyThere;
  // Another domain is fine when the directory holds that exact address: the
  // person has signed in to this workspace, which is what the access gate
  // actually checks. Until the search has answered, it is not yet known.
  const { canAdd, refusal: outsideWorkspace } = judgeTypedEntry({
    typed: trimmed,
    alreadyThere,
    ownDomain,
    directory: settled && !search.isError ? search.people : null,
    searchFailed: settled && search.isError,
  });
  // The last entry's lock reason: the caller's for a real colleague, ours for
  // a bare word saved before only emails were accepted (FRONT-2756).
  let loneReason: string | null = null;
  if (people.length === 1 && lastEntryLocked) {
    loneReason = isValidEntry(people[0]) ? lastEntryLocked : LONE_STALE_ENTRY_REASON;
  }

  function add() {
    if (!canAdd) return;
    onAdd(trimmed);
    setQuery('');
    setHighlight(-1);
  }

  function pick(person: PersonSuggestion) {
    onAdd(person.email.trim().toLowerCase());
    setQuery('');
    setHighlight(-1);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    const isArrow = e.key === 'ArrowDown' || e.key === 'ArrowUp';
    if (listOpen && isArrow) {
      e.preventDefault();
      const next = moveHighlightIndex(highlight, e.key === 'ArrowDown' ? 'down' : 'up', suggestions.length);
      setHighlight(next);
      // Keyboard moves only: the list scrolls, and a walk past its bottom edge
      // must not land on a row the person cannot see.
      document.getElementById(`${listboxId}-option-${next}`)?.scrollIntoView({ block: 'nearest' });
      return;
    }
    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
    e.preventDefault();
    const action = decidePeopleEnter({
      typed: trimmed,
      valid,
      outsideWorkspace: !canAdd,
      listOpen,
      rows: suggestions,
      highlight,
    });
    if (action.kind === 'pick') {
      pick(suggestions[action.index]);
    } else if (action.kind === 'add') {
      add();
    }
  }

  /** The one thing worth saying under the field about what is typed, or null. */
  const blocked = (() => {
    if (!trimmed) return null;
    if (alreadyThere) return 'Already on the list.';
    if (!trimmed.includes('@')) {
      // A name being searched for: say something only once the search has
      // answered, so nothing flickers while typing.
      if (trimmed.length < MIN_PEOPLE_QUERY_LENGTH || !settled) return null;
      if (search.isError) return 'Could not search your colleagues. Enter their email address instead.';
      if (suggestions.length > 0) return null;
      if (search.people.length > 0) return 'Already on the list.';
      return 'No colleague found. Pick one from the list, or enter their email address.';
    }
    if (!isValidEntry(trimmed)) return 'Enter a full email address.';
    return null;
  })();

  return (
    <div className="space-y-1.5 min-w-0">
      {people.length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center">
          {people.map((entry) => {
            const locked = loneReason;
            // FRONT-2756: a bare word saved before only emails were accepted.
            // It matches nobody, so it says so rather than passing for a
            // colleague; removing it is the owner's call, never silent.
            const stale = !isValidEntry(entry);
            return (
              <span
                key={entry}
                className={cn(
                  'inline-flex max-w-full items-center gap-1 rounded-md px-2 py-0.5 text-xs border',
                  stale
                    ? 'bg-destructive/10 text-destructive border-destructive/30'
                    : 'bg-muted text-foreground border-border',
                )}
              >
                <span className="truncate">{entry}</span>
                {stale && <span className="shrink-0">· not an email, remove</span>}
                <button
                  type="button"
                  onClick={() => onRemove(entry)}
                  disabled={disabled || !!locked}
                  aria-label={locked ? `Cannot remove ${entry} — ${locked}` : `Remove ${entry}`}
                  className={cn(
                    'shrink-0 text-muted-foreground',
                    locked || disabled
                      ? 'opacity-40 cursor-not-allowed'
                      : 'hover:text-foreground cursor-pointer',
                  )}
                >
                  <XIcon size={11} weight="bold" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-1.5 min-w-0">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={disabled}
          placeholder="Name or email"
          aria-label="Add a colleague by name or email"
          role="combobox"
          aria-expanded={listOpen}
          aria-controls={listOpen ? listboxId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={listOpen && highlight >= 0 ? `${listboxId}-option-${highlight}` : undefined}
          autoComplete="off"
          className="h-8 min-w-0 flex-1 text-xs"
        />
        <Button
          variant="outline"
          size="xs"
          onClick={add}
          disabled={disabled || !canAdd}
          className="shrink-0"
        >
          <PlusIcon size={11} weight="bold" />
          Add
        </Button>
      </div>

      {listOpen && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Colleagues"
          className="flex max-h-48 flex-col gap-0.5 overflow-y-auto rounded-md border border-border bg-background p-1"
        >
          {suggestions.map((person, i) => (
            <li
              key={person.userId}
              id={`${listboxId}-option-${i}`}
              role="option"
              aria-selected={i === highlight}
              // Keeps focus in the field, so the blur that closes the list
              // never fires before the click lands.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(person)}
              onMouseEnter={() => setHighlight(i)}
              className={cn(
                'flex min-w-0 cursor-pointer items-center gap-2 rounded-sm px-2 py-1 text-xs leading-tight transition-colors',
                i === highlight ? 'bg-muted' : 'hover:bg-muted/50',
              )}
            >
              <UserCircleIcon size={14} className="shrink-0 text-muted-foreground" />
              {/* Name above address: the popover is narrow, and on one line
                  the address — the part that tells two Matts apart — is what
                  got truncated. */}
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-foreground">{person.name ?? person.email}</span>
                {person.name && <span className="truncate text-muted-foreground">{person.email}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Exactly one line under the field, and only when there is something
          to say. FRONT-1880 (Marko, 2026-09-09) cut the standing paragraph
          that explained workspace-only on every render: "Only specific
          colleagues" says that in two words, so the explanation was cost
          without information. What is left fires when it applies. */}
      {outsideWorkspace ? (
        <p className="text-xs text-amber-600 dark:text-amber-300">{outsideWorkspace}</p>
      ) : blocked ? (
        <p className="text-xs text-muted-foreground">{blocked}</p>
      ) : (
        loneReason && <p className="text-xs text-muted-foreground">{loneReason}</p>
      )}
    </div>
  );
}
